import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { readSeenPosts, compactSeenPosts } from './feedPreferences';

// A separate, fixed session keeps Discover's ordering and pagination untouched.
export default function useSwipeFeed(enabled) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);
  const [completedPage, setCompletedPage] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const pending = useRef(false);
  const exhausted = useRef({ mixed: false, videos: false });
  const session = useRef(null);
  const mixedEndpoint = useRef('reelPosts');
  const cursors = useRef({ mixed: 1, videos: 1 });
  const knownIds = useRef(new Set());
  const loadMore = useCallback(() => {
    if (pending.current || !hasMore) return;
    pending.current = true;
    setPage(value => value + 1);
  }, [hasMore]);
  const retry = useCallback(() => { setAttempt(value => value + 1); }, []);
  useEffect(() => { if (enabled && page === 0) loadMore(); }, [enabled, page, loadMore]);
  useEffect(() => {
    if (!page) return;
    if (!session.current) {
      const seen = readSeenPosts();
      session.current = { asOf: Date.now(), seed: Math.floor(Math.random() * 2147483647), seenPostIds: seen.length > 500 ? [] : seen, seenPostRanges: seen.length > 500 ? compactSeenPosts(seen) : undefined };
    }
    const controller = new AbortController();
    setLoading(true); setError(false); pending.current = true;
    const request = (endpoint, limit, streamPage) => axios.post(`${API_URL}/api/v1/posts/feed`, { ...session.current, endpoint, page: streamPage, limit }, { signal: controller.signal, timeout: 30000 })
      .then(({data}) => { if (!Array.isArray(data)) throw new Error('Invalid feed'); return data; });
    const mixed = () => request(mixedEndpoint.current, 8, cursors.current.mixed).catch(failure => {
      // Older running servers do not yet recognize reelPosts. Keep browsing
      // through the established endpoint rather than failing the whole viewer.
      if (controller.signal.aborted || mixedEndpoint.current !== 'reelPosts' || ![400, 404, 405, 501].includes(failure.response?.status)) throw failure;
      mixedEndpoint.current = 'allPosts';
      return request('allPosts', 8, cursors.current.mixed);
    });
    const tasks = [exhausted.current.mixed ? Promise.resolve(null) : mixed(), exhausted.current.videos ? Promise.resolve(null) : request('videoPosts', 2, cursors.current.videos)];
    Promise.allSettled(tasks).then(results => {
      if (controller.signal.aborted) return;
      const batches = [[], []];
      results.forEach((result, index) => {
        if (result.status !== 'fulfilled' || result.value === null) return;
        const stream = index === 0 ? 'mixed' : 'videos';
        batches[index] = result.value;
        exhausted.current[stream] = result.value.length < (index === 0 ? 8 : 2);
        cursors.current[stream] += 1;
      });
      setCompletedPage(page);
      setHasMore(!exhausted.current.mixed || !exhausted.current.videos);
      const [mixedPosts, videos] = batches;
      const combined = [...mixedPosts];
      videos.forEach((item, index) => combined.splice(Math.min(combined.length, index * 4 + 1), 0, item));
      const seenNow = new Set(readSeenPosts());
      const fresh = combined.filter(item => {
        const id = String(item.post_id);
        if (knownIds.current.has(id) || seenNow.has(id)) return false;
        knownIds.current.add(id); return true;
      });
      if (fresh.length) setPosts(previous => [...previous, ...fresh]);
      // A failed video batch must not discard successfully loaded image posts,
      // and each stream retries its own page without skipping any content.
      setError(!fresh.length && results.some(result => result.status === 'rejected'));
    }).finally(() => { if (!controller.signal.aborted) { pending.current = false; setLoading(false); } });
    return () => controller.abort();
  }, [page, attempt]);
  return { posts, loading, error, hasMore, loadMore, retry, page: completedPage };
}
